import { listNotifications } from '../api/notifications.ts';
import {
  listNotices,
  mergeServerNotices,
  readNotificationsCursor,
  writeNotices,
  writeNotificationsCursor,
} from './store.ts';

/**
 * Pull-based notification sync (tenant #116, the protocol the desktop
 * client already runs against dev).
 *
 * One round:
 *   1. `GET /v1/notifications?since=<cursor>&limit=100`, cursor as the
 *      raw stored string (microsecond precision — never through `Date`).
 *      No cursor yet = first sync = historical backfill, merged as
 *      already-read.
 *   2. Merge the page (fingerprint string-equality dedup), advance the
 *      cursor to the last row's `lastSeenAt` (pages are ascending, so
 *      the last one is the max), persist both.
 *   3. Repeat while pages come back full, capped at MAX_SYNC_PAGES so a
 *      misbehaving server cannot spin the loop forever.
 *
 * Errors are the caller's problem (react-query records them); the store
 * keeps whatever the last successful round persisted, so the UI
 * degrades to locally stored items instead of blanking.
 */

export const NOTIFICATIONS_PAGE_LIMIT = 100;
const MAX_SYNC_PAGES = 10;

export async function syncNotifications(scope: string, fetchImpl: typeof fetch): Promise<number> {
  let cursor = readNotificationsCursor(scope);
  const backfill = cursor.length === 0;
  let received = 0;
  for (let page = 0; page < MAX_SYNC_PAGES; page += 1) {
    const response = await listNotifications(
      { since: cursor.length > 0 ? cursor : undefined, limit: NOTIFICATIONS_PAGE_LIMIT },
      fetchImpl,
    );
    const items = response.notifications;
    if (items.length > 0) {
      received += items.length;
      writeNotices(scope, mergeServerNotices(listNotices(scope), items, { markRead: backfill }));
      for (const item of items) {
        if (item.lastSeenAt.length > 0) {
          cursor = item.lastSeenAt;
        }
      }
      writeNotificationsCursor(scope, cursor);
    }
    if (items.length < NOTIFICATIONS_PAGE_LIMIT) {
      break;
    }
  }
  return received;
}
